import { differenceInYears, parse, getYear } from 'date-fns'
import { declensionByNum } from 'www/utils'
import i18next from 'i18next'
import { YEAR_CASES } from 'www/constants/wordCases'
import { MIN_BIRTH_YEAR } from 'www/vue_components/common/AppointmentPage/constants'

/**
 * @callback Rule
 * @param { any } val -
 *
 * @returns { true | string } -
 */
/** @typedef { Rule } RequiredRule */
/** @typedef { Rule } AgeLimitRule */

/**
 *
 * @param { { min?: number, max?: number }[] } rangeAgeLimits -
 * @param {number} age -
 * @param {boolean} isServices -
 */
export function getBirthDayValidationHint(rangeAgeLimits = [], age = null, isServices = false) {
  const extremeRangeAgeLimits = rangeAgeLimits.reduce((result, current) => {
    if (typeof current.min !== 'number') {
      result.min = -Infinity
    } else if (current.min < result.min) {
      result.min = current.min
    }

    if (typeof current.max !== 'number') {
      result.max = Infinity
    } else if (current.max > result.max) {
      result.max = current.max
    }

    return result
  }, { min: Infinity, max: -Infinity })

  const { min, max } = extremeRangeAgeLimits
  const options = { ns: i18next.language }

  if (min && Number.isFinite(min) && (age < min || age === null)) {
    const minYearsDeclension = i18next.t(declensionByNum(min, YEAR_CASES.genitive), options)

    return isServices
      ? `Услуга только для пациентов с ${min} ${minYearsDeclension}`
      : `${i18next.t('Врач принимает только пациентов с {age} {declension}', { ...options, age: min, declension: minYearsDeclension })}`
  }

  if (max && Number.isFinite(max) && (age >= max || age === null)) {
    const maxYearsDeclension = i18next.t(declensionByNum(max, YEAR_CASES.genitive), options)

    return isServices
      ? `Услуга только для пациентов до ${max} ${maxYearsDeclension}`
      : `${i18next.t('Врач принимает только пациентов до {age} {declension}', { ...options, age: max, declension: maxYearsDeclension })}`
  }

  return ''
}

/**
 * @param { { min?: number, max?: number }[] } rangeAgeLimits -
 * @param { { isServices: boolean } } options -
 *
 * @returns { { required: RequiredRule, ageLimit: AgeLimitRule } } -
 */
const getBirthDayValidationRulesAllSpecialties = (rangeAgeLimits = [], options = {}) => {
  const { isServices = false } = options

  /** @type { RequiredRule } */
  const required = val => !!val || i18next.t('Укажите дату рождения в формате ДД.ММ.ГГГГ', { ns: i18next.language })

  /** @type { AgeLimitRule } */
  const ageLimit = val => {
    const now = new Date()
    const selectedDate = parse(val, 'dd.MM.yyyy', new Date())
    const age = differenceInYears(now, selectedDate)
    const birthYear = getYear(selectedDate)

    if (
      Number.isNaN(age)
        || birthYear < MIN_BIRTH_YEAR
        || age < 0
        || (now < selectedDate)
    ) {
      return i18next.t('Проверьте дату рождения', { ns: i18next.language })
    }

    const rangeAgeLimitsFiltered = rangeAgeLimits.filter(i => i)
    const isAllFailedValidation = rangeAgeLimitsFiltered.every(({ min, max }) => (
      (min && age < min)
      || (max && age >= max)
    ))

    if (isAllFailedValidation && rangeAgeLimitsFiltered.length) {
      return getBirthDayValidationHint(rangeAgeLimitsFiltered, age, isServices)
    }

    return true
  }

  return { required, ageLimit }
}

export default getBirthDayValidationRulesAllSpecialties
